interface InviteLinkControls {
  button: Pick<HTMLButtonElement, 'hidden' | 'disabled'>;
  fallback: Pick<HTMLElement, 'hidden'>;
  input: Pick<HTMLInputElement, 'value' | 'focus' | 'select'>;
}

/** Copy feedback belongs to one crew, even if a browser permission prompt is slow. */
export class InviteLink {
  private link = '';
  private revision = 0;
  private copying = false;

  public constructor(
    private readonly controls: InviteLinkControls,
    private readonly announce: (message: string) => void,
    private readonly origin = location.origin,
    private readonly writeText: (text: string) => Promise<void> = text => navigator.clipboard.writeText(text),
  ) {
    controls.button.hidden = true;
    controls.fallback.hidden = true;
    controls.input.value = '';
  }

  public setCrew(code: string | null): void {
    const link = code ? `${this.origin}/join/${encodeURIComponent(code)}` : '';
    if (link === this.link) return;
    this.link = link;
    this.revision++;
    this.copying = false;
    this.controls.button.hidden = !link;
    this.controls.button.disabled = false;
    this.controls.fallback.hidden = true;
    this.controls.input.value = link;
    this.announce('');
  }

  public async copy(): Promise<void> {
    if (!this.link || this.copying) return;
    const revision = this.revision;
    this.copying = true;
    this.controls.button.disabled = true;
    // Manual sharing remains possible while a permission prompt is unanswered.
    this.controls.fallback.hidden = false;
    try {
      // Keep invocation inside try: unavailable clipboard APIs can throw
      // synchronously, while denied permissions reject asynchronously.
      await this.writeText(this.link);
      if (revision !== this.revision) return;
      this.controls.fallback.hidden = true;
      this.announce('INVITE LINK COPIED.');
    } catch {
      if (revision !== this.revision) return;
      this.controls.fallback.hidden = false;
      this.controls.input.focus();
      this.controls.input.select();
      this.announce('COPY THE SELECTED LINK TO INVITE FRIENDS.');
    } finally {
      if (revision === this.revision) {
        this.copying = false;
        this.controls.button.disabled = false;
      }
    }
  }
}
